import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Pagination, PAGE_SIZE } from "@/components/Pagination";
import {
  Plus,
  Stack,
  PencilSimple,
  Rows,
  SquaresFour,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { AppHeader } from "@/components/AppHeader";
import { FilterBar } from "@/components/FilterBar";
import { BatchBar } from "@/components/BatchBar";
import { DensityToggle, useDensity } from "@/components/DensityToggle";
import { Thumb } from "@/components/Thumb";
import { toast } from "sonner";
import { api } from "@/lib/api";

const FILTERS = ["All", "Available", "Reserved", "Used"];
const BADGE = {
  Available: "bg-emerald-500/10 text-emerald-400 border-emerald-500/25",
  Reserved: "bg-yellow-500/10 text-yellow-400 border-yellow-500/25",
  Used: "bg-zinc-500/10 text-zinc-400 border-zinc-500/25",
};

const MiniSlab = ({ r }) => {
  const W = 120;
  const H = Math.max(30, Math.min(90, (r.width_in / r.length_in) * W));
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="h-16 w-28 shrink-0 rounded border border-zinc-700 bg-zinc-950"
    >
      <rect
        x="0.5"
        y="0.5"
        width={W - 1}
        height={H - 1}
        fill="none"
        stroke="#52525b"
      />
      {(r.polygon || []).length > 2 && (
        <polygon
          points={r.polygon.map((p) => `${p.x * W},${p.y * H}`).join(" ")}
          fill="rgba(109,93,246,0.25)"
          stroke="#6d5df6"
          strokeWidth="2"
        />
      )}
    </svg>
  );
};

export default function Inventory() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [density, setDensity, compact] = useDensity();
  const [view, setView] = useState(
    () => localStorage.getItem("bg_remnants_view") || "list",
  );
  const [fit, setFit] = useState({ l: "", w: "" });
  useEffect(() => localStorage.setItem("bg_remnants_view", view), [view]);
  const fitsPiece = (r) => {
    const L = Number(fit.l);
    const W = Number(fit.w);
    if (!L || !W) return true;
    const poly = r.polygon || [];
    const bl =
      poly.length > 2
        ? (Math.max(...poly.map((p) => p.x)) -
            Math.min(...poly.map((p) => p.x))) *
          r.length_in
        : r.length_in;
    const bw =
      poly.length > 2
        ? (Math.max(...poly.map((p) => p.y)) -
            Math.min(...poly.map((p) => p.y))) *
          r.width_in
        : r.width_in;
    return (L <= bl && W <= bw) || (W <= bl && L <= bw);
  };

  const shown = items
    .filter(fitsPiece)
    .filter((r) => filter.length === 0 || filter.includes(r.status));

  const totalPages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const paged = shown.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);

  const cycleStatus = async (r) => {
    const order = ["Available", "Reserved", "Used"];
    const next = order[(order.indexOf(r.status) + 1) % order.length];
    try {
      await api.patch(`/remnants/${r.id}/status`, { status: next });
      toast.success(`${r.code} → ${next}`);
      load();
    } catch {
      toast.error("Could not change status");
    }
  };

  const toggle = (id) =>
    setSelected((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
    );

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (q.trim()) params.q = q.trim();
    const [list, st] = await Promise.all([
      api.get("/remnants", { params }),
      api.get("/remnants/stats"),
    ]);
    setItems(list.data);
    setStats(st.data);
    setLoading(false);
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Inventory / Remnants" />
      <main className="relative z-10 mx-auto max-w-6xl px-4 pb-28 pt-6">
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="remnants-header-card"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Inventory / Remnants
              </div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Remnant Intake
              </h1>
              <p className="mt-1 text-sm text-zinc-500">
                Leftover slabs with traced usable fields and square footage
              </p>
            </div>
          </div>

          <div
            className="mt-5 hidden flex-wrap gap-2 border-t border-zinc-800 pt-4 sm:flex"
            data-testid="remnants-actions"
          >
            <Button
              data-testid="log-remnant-button"
              onClick={() => navigate("/inventory/remnants/new")}
              className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <Plus size={15} weight="bold" className="mr-2" /> Log Remnant
            </Button>
          </div>
        </div>

        {stats && (
          <div className="mt-6 hidden grid-cols-2 gap-3 sm:grid sm:grid-cols-4">
            {[
              ["Remnants", stats.total, "text-white"],
              ["Usable Sq Ft", stats.usable_sqft, "text-yellow-400"],
              [
                "Available",
                `${stats.available} / ${stats.available_sqft} ft²`,
                "text-emerald-400",
              ],
              [
                "Reserved",
                `${stats.reserved} / ${stats.reserved_sqft} ft²`,
                "text-blue-400",
              ],
            ].map(([k, v, c]) => (
              <div
                key={k}
                data-testid={`remnant-stat-${k.toLowerCase().replace(/ /g, "-")}`}
                className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              >
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  {k}
                </div>
                <div className={`font-mono-data mt-1 text-xl font-bold ${c}`}>
                  {v}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-start">
          <FilterBar
            className="min-w-0 flex-1"
            testidPrefix="remnants"
            search={{
              value: q,
              onChange: setQ,
              placeholder: "Search color, rack, code, source job…",
              testid: "remnant-search-input",
            }}
            sections={[
              {
                key: "status",
                label: "Status",
                multi: true,
                values: filter,
                allValue: "All",
                onChange: setFilter,
                options: FILTERS.map((f) => [
                  f,
                  f,
                  `remnant-filter-${f.toLowerCase()}`,
                ]),
              },
            ]}
          />
          <div className="flex gap-2 overflow-x-auto pb-1">
            <div className="flex shrink-0 overflow-hidden rounded-md border border-zinc-800">
              {[
                ["list", Rows],
                ["grid", SquaresFour],
              ].map(([k, Icon]) => (
                <button
                  key={k}
                  data-testid={`remnants-view-${k}`}
                  onClick={() => setView(k)}
                  title={`${k} view`}
                  className={`flex h-12 items-center gap-2 px-3 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                    view === k
                      ? "bg-indigo-500 text-white"
                      : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                  }`}
                >
                  <Icon size={16} weight="bold" /> {k}
                </button>
              ))}
            </div>
            {view === "list" && (
              <DensityToggle density={density} onChange={setDensity} />
            )}
          </div>
        </div>

        <div
          className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-3"
          data-testid="fit-finder"
        >
          <span className="text-[11px] uppercase tracking-widest text-zinc-500">
            Fit finder — piece size (in)
          </span>
          <Input
            data-testid="fit-length-input"
            type="number"
            value={fit.l}
            onChange={(e) => setFit({ ...fit, l: e.target.value })}
            placeholder="Length"
            className="h-11 w-28 border-zinc-800 bg-zinc-950"
          />
          <span className="text-zinc-600">x</span>
          <Input
            data-testid="fit-width-input"
            type="number"
            value={fit.w}
            onChange={(e) => setFit({ ...fit, w: e.target.value })}
            placeholder="Depth"
            className="h-11 w-28 border-zinc-800 bg-zinc-950"
          />
          {(fit.l || fit.w) && (
            <>
              <span
                className="font-mono-data text-xs text-yellow-400"
                data-testid="fit-result-count"
              >
                {shown.length} fit
              </span>
              <button
                data-testid="fit-clear"
                onClick={() => setFit({ l: "", w: "" })}
                className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-white"
              >
                Clear
              </button>
            </>
          )}
        </div>

        {view === "grid" && (
          <div
            className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            data-testid="remnant-grid"
          >
            {paged.map((r) => (
              <div
                key={r.id}
                className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 transition-colors duration-150 hover:border-zinc-700"
              >
                <Link
                  to={`/inventory/remnants/${r.id}`}
                  data-testid={`remnant-tile-${r.id}`}
                >
                  <Thumb
                    item={r}
                    testId={`remnant-thumb-${r.id}`}
                    className="h-40"
                  />
                  <div className="flex items-start justify-between gap-2 p-3 pb-0">
                    <div className="min-w-0">
                      <div className="font-mono-data text-xs text-yellow-500">
                        {r.code}
                      </div>
                      <h3 className="truncate text-sm font-bold">
                        {r.slab_color}
                      </h3>
                      <div className="mt-1 text-xs text-zinc-400">
                        {r.length_in}" x {r.width_in}" · {r.rack || "No rack"}
                      </div>
                    </div>
                    <span
                      className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                        BADGE[r.status] || BADGE.Available
                      }`}
                    >
                      {r.status}
                    </span>
                  </div>
                </Link>
                <div className="mt-3 flex items-center justify-between border-t border-zinc-800 px-3 py-2">
                  <span className="font-mono-data text-xs text-yellow-400">
                    {r.usable_sqft} ft²
                  </span>
                  <button
                    data-testid={`remnant-grid-edit-${r.id}`}
                    onClick={() => navigate(`/inventory/remnants/${r.id}/edit`)}
                    className="text-zinc-500 hover:text-yellow-400"
                  >
                    <PencilSimple size={15} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div
          className={`mt-6 ${compact ? "space-y-1.5" : "space-y-3"} ${view === "grid" ? "hidden" : ""}`}
          data-testid="remnant-list"
        >
          {loading &&
            [0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-24 animate-pulse rounded-lg border border-zinc-800 bg-zinc-900"
              />
            ))}
          {!loading && shown.length === 0 && (
            <div
              data-testid="remnant-empty-state"
              className="rounded-lg border border-dashed border-zinc-800 p-10 text-center"
            >
              <Stack
                size={28}
                weight="bold"
                className="mx-auto text-zinc-700"
              />
              <p className="mt-3 text-sm text-zinc-400">
                {fit.l || fit.w
                  ? `Nothing on the racks fits ${fit.l || "?"}" x ${fit.w || "?"}" — try a smaller piece.`
                  : "No remnants logged yet. Trace a leftover slab to start the log."}
              </p>
            </div>
          )}
          {!loading &&
            paged.map((r) => (
              <div
                key={r.id}
                className={`group flex items-center gap-3 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 transition-all duration-150 hover:-translate-y-px hover:border-zinc-700 ${
                  compact ? "py-2 pr-3" : "py-4 pr-4"
                }`}
              >
                <span
                  className={`w-1 shrink-0 self-stretch ${
                    r.status === "Available"
                      ? "bg-emerald-400"
                      : r.status === "Reserved"
                        ? "bg-yellow-400"
                        : "bg-zinc-600"
                  }`}
                />
                <span className={compact ? "pl-2" : "pl-3"}>
                  <Checkbox
                    data-testid={`remnant-select-${r.id}`}
                    checked={selected.includes(r.id)}
                    onCheckedChange={() => toggle(r.id)}
                    className="h-5 w-5 border-zinc-600 data-[state=checked]:border-indigo-500 data-[state=checked]:bg-indigo-500 data-[state=checked]:text-white"
                  />
                </span>
                <Link
                  to={`/inventory/remnants/${r.id}`}
                  data-testid={`remnant-card-${r.id}`}
                  className={`flex min-w-0 flex-1 items-center ${compact ? "gap-3" : "gap-4"}`}
                >
                  {!compact && (
                    <span className="hidden sm:block">
                      <MiniSlab r={r} />
                    </span>
                  )}
                  <div
                    className={
                      compact
                        ? "flex min-w-0 flex-1 items-center gap-4"
                        : "min-w-0 flex-1"
                    }
                  >
                    <div className="font-mono-data text-xs text-yellow-500">
                      {r.code}
                    </div>
                    <h3
                      className={
                        compact
                          ? "truncate text-sm font-bold"
                          : "truncate text-lg font-bold"
                      }
                    >
                      {r.slab_color}
                    </h3>
                    <div
                      className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-400 ${
                        compact ? "min-w-0 flex-1" : "mt-1 gap-x-5"
                      }`}
                    >
                      <span className="font-mono-data inline-flex items-center rounded-full bg-zinc-950 px-2 py-0.5">
                        {r.length_in}" x {r.width_in}" · {r.thickness}
                      </span>
                      <span className="hidden items-center gap-1 rounded-full bg-zinc-950 px-2 py-0.5 sm:inline-flex">
                        <span className="text-zinc-600">Rack</span>
                        {r.location || "—"}
                      </span>
                      {r.source_tag && (
                        <span className="hidden items-center gap-1 rounded-full bg-zinc-950 px-2 py-0.5 sm:inline-flex">
                          <span className="text-zinc-600">From</span>
                          {r.source_tag}
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    className={
                      compact
                        ? "flex shrink-0 items-center gap-3"
                        : "shrink-0 text-right"
                    }
                  >
                    <div
                      className={`font-mono-data font-bold text-yellow-400 ${
                        compact ? "text-sm" : "text-lg"
                      }`}
                    >
                      {Math.round(
                        ((r.usable_sqft || 0) - (r.allocated_sqft || 0)) * 10,
                      ) / 10}{" "}
                      ft²
                    </div>
                    {r.allocated_sqft > 0 && (
                      <div className="font-mono-data text-[10px] text-zinc-500">
                        {r.allocated_sqft} allocated
                      </div>
                    )}
                    <span
                      className={`inline-flex rounded-full border font-semibold uppercase tracking-wider ${
                        BADGE[r.status] || BADGE.Available
                      } ${compact ? "px-2 py-0.5 text-[10px]" : "mt-1 px-3 py-1 text-xs"}`}
                    >
                      {r.status}
                    </span>
                  </div>
                </Link>
                <button
                  data-testid={`remnant-status-${r.id}`}
                  onClick={() => cycleStatus(r)}
                  title="Change status"
                  className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-400 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-300 sm:flex"
                >
                  <ArrowsClockwise size={16} weight="bold" />
                </button>
                <button
                  data-testid={`remnant-edit-${r.id}`}
                  onClick={() => navigate(`/inventory/remnants/${r.id}/edit`)}
                  title="Edit remnant"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-400 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-300"
                >
                  <PencilSimple size={18} weight="bold" />
                </button>
              </div>
            ))}
        </div>
        <Pagination page={curPage} total={shown.length} onPage={setPage} testId="inventory-pagination" />
      </main>

      <div className="no-print fixed bottom-0 left-0 right-0 z-20 border-t border-zinc-800 bg-zinc-950 p-3 sm:hidden">
        <Button
          data-testid="log-remnant-button-mobile"
          onClick={() => navigate("/inventory/remnants/new")}
          className="h-12 w-full bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
        >
          <Plus size={18} weight="bold" className="mr-2" /> Log Remnant
        </Button>
      </div>

      <BatchBar
        selected={selected}
        onClear={() => setSelected([])}
        type="remnants"
      />
    </div>
  );
}
